<template>
  <div class="integrapay-card-storage">
    <loading-spinner v-if="processing">
      <div slot="title">
        Storing credit card details
      </div>
      <div slot="message">
        Please wait while we encrypt and store your card details securely.
      </div>
    </loading-spinner>
    <storecard-complete
      v-if="internalSuccessMessage"
      :card-details="savedCardDetails"
    />
    <transaction-error
      v-if="internalFailureMessage"
      :error="errorDetails"
    />
    <storecard-error
      v-if="storecardException"
      :details="errorDetails"
    />
    <div
      v-show="!processing"
      class="credit-carrd-form-wrapper"
    >
      <creditCardForm
        v-model="newCard"
        :show-card-save="false"
        :show-override-amount="false"
        :payment-testing="paymentTesting"
        @hasErrors="setHasdDetailsErrors"
        @cardValid="cardValidState"
      />
    </div>
    <b-container
      v-if="inputConfig.showSaveButton"
      class="store-card-button"
    >
      <b-row
        v-show="!processing"
        class="justify-content-center"
      >
        <b-col>
          <payment-button
            :disabled="disableStoreCardButton"
            :recaptcha="config.recaptcha"
            :tooltip-message="storeCardMessage"
            :label="buttonLabel"
            @recaptchaVerified="(v) => recaptchaResponse = v"
            @click="processStoreCard"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { StoreCardError } from './errors'

import creditCardForm from './creditCardForm.vue'
import commonMixins from './mixins/common'
import integrapayMixins from './mixins/integrapay.js'

import PaymentButton from './payment-button.vue'
import storecardComplete from './modals/storecard-complete.vue'
import storecardError from './modals/storecard-error.vue'
import errorMessage from './modals/error-messages'
import transactionError from './modals/transaction-error.vue'

const defaultConfig = {
  buttonLabel: 'Store Card Details',
  showSaveButton: true,
  paymentTesting: false,
  showErrors: true,
  errorHandling: 'internal',
  recaptcha: false,
  internalSuccessMessage: true,
  internalFailureMessage: true,
  paymentProcessor: 'integrapay',
  paymentProcessorOptions: {
    integrapay: {}
  }

}

export default {
  name: 'integrapay-card-storage',
  components: {
    creditCardForm,
    'payment-button': PaymentButton,
    storecardComplete,
    storecardError,
    transactionError
  },
  mixins: [
    commonMixins,
    integrapayMixins
  ],
  props: {
    eventListener: {
      type: Function,
      required: false,
      default: null
    },
    callback: {
      type: Function,
      required: false,
      default: null
    },
    inputConfig: {
      type: Object,
      required: false,
      default: () => (defaultConfig)
    }
  },
  data() {
    return {
      gqlClient: null,
      state: 'preparing',
      recaptchaResponse: null,
      storecardProcessor: null,
      savedCardDetails: null,
      errorDetails: null,
      newCard: {
        cardholderName: '',
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvc: '',
        cardType: null,
        cardValid: false,
        cardSave: false
      }
    }
  },
  computed: {
    showRecaptchaButton: function() {
      return (this.config.recaptcha && typeof this.config.recaptcha.key === 'string' && this.config.recaptcha.key.length > 0)
    },
    recaptchaVerified: function() {
      if (this.showRecaptchaButton) {
        return this.recaptchaResponse !== null
      }
      else {
        return true
      }
    },
    processing() {
      return (this.state === 'processing')
    },
    complete() {
      return (this.state === 'complete')
    },
    storecardFailed() {
      return (this.state === 'storecard-failed')
    },
    internalSuccessMessage() {
      return this.config.internalSuccessMessage && this.complete
    },
    internalFailureMessage() {
      return this.config.internalFailureMessage && this.storecardFailed
    },
    storecardException() {
      return this.state === 'processing-exception' && this.config.errorHandling === 'internal'
    },
    enableStoreCard() {
      return this.newCard.cardValid && this.config.showSaveButton && this.state === 'card-entry'
    },
    disableStoreCardButton() {
      return !this.newCard.cardValid || this.processing
    },
    buttonLabel() {
      return this.config.buttonLabel
    },
    config() {
      // eslint-disable-next-line no-undef
      return { ...defaultConfig, ...this.inputConfig }
    },
    storeCardMessage: function() {
      if (this.hasCardDetailsErrors) {
        return 'You need to resolve the errors with the card details.'
      }
      else if (!this.newCard.cardValid) {
        return 'You need to complete the Credit Card Details to Continue.'
      }
      else if (!this.recaptchaVerified) {
        return 'You need to complete the Captcha before continuing.'
      }
      else {
        return 'Securely Store Card Details'
      }
    }
  },
  created() {
    this.$on('store-card', (recaptchaResponse, cb) => {
      if (this.newCard.cardValid) {
        let payer = this.processStoreCard(recaptchaResponse)
        if (typeof cb === 'function') {
          cb.call(this, payer)
        }
      }
    })
  },
  mounted() {
    this.setState('preparing')
    this.setPaymentTestingCardDetails()
    // Initialize the payment Processor
    switch (this.config.paymentProcessor) {
    case 'integrapay':
    default:
      this.storecardProcessor = this.integrapayClient()
      break
    }

    if (this.config.accountUID) {
      this.account = this.config.accountUID
      this.setState('card-entry')
    }
    else {
      this.getCurrentLoggedInAccount()
        .then(userid => {
          this.account = userid
          this.setState('card-entry')
        })
    }
  },
  methods: {
    setErrorDetails: function(err) {
      this.$set(this, 'errorDetails', err)
    },
    handleStoreCardException: function(err) {
      this.$emit('error', ['processStoreCard', err.message])
      this.setState('processing-exception')
      this.setErrorDetails(err)
    },
    handleStorecardFailure: function(errors) {
      if (this.config.errorHandling === 'internal') {
        this.setErrorDetails(errors)
        this.setState('storecard-failed')
      }
      this.$emit('storecard-failed', [errors])
    },

    processStoreCreditCard() {
      return this.processStoreCard(null)
    },
    processStoreCard(recaptchaResponse) {
      this.setState('processing')
      this.$emit('processing')

      if (typeof this.callback === 'function') {
        return this.storecardProcessor.tokenizeCard(this.newCard)
          .then(cardToken => {
            this.setState('complete')
            return this.callback(this, cardToken, recaptchaResponse)
          })
      }
      this.storecardProcessor.storeCreditCard(this.newCard, { authorization: recaptchaResponse })
        .then(payer => {
          this.$emit('success', [payer])
          this.setState('complete')
          this.$set(this, 'savedCardDetails', payer)
        })
        .catch(err => {
          const handleError = function(errMessage) {
            this.$emit('failed', ['storeCardProcessing', errMessage, err.details[0], err.stack])
            this.setErrorDetails(errMessage)
            this.setState('storecard-failed')
          }

          if (err instanceof StoreCardError) {
            handleError.call(this, errorMessage(err.details[0].category))
          }
          else if (err.constructor.name === 'GenericError') {
            handleError.call(this, { title: 'Generic Erorr', msg: 'There was an error loading the transaction for payment.' })
          }
          else {
            this.handleStoreCardException(err)
          }
        })
    }
  }
}
</script>

<style scoped lang="scss">
  .cc-details {
    display: block;
  }
</style>
